export type TextAlign = "Left" | "Right" | "Center" | "Justify";
import React, { ReactElement } from "react";
import type { TranslatedString } from "@i18n";

export interface TooltipProps {
  enable: boolean;
}

export enum BorderStyleTypes {
  DOUBLE = "double",
  SOLID = "solid",
  GROOVE = "groove",
  DOTTED = "dotted",
  DASHED = "dashed",
  RIDGE = "ridge",
  INSET = "inset",
}

export enum MaskTypes {
  MOBILE = "000-000-0000",
  COUNTRY_ISO_CODE = "LL/LLL",
  DATE = "00/00/0000",
  PRODUCT_KEY = "AAAAA-AAAAA-AAAAA-AAAAA",
  DATE_FORMAT_2 = "00/LLL/0000",
}

export type ButtonComponentProps = {
  cssClass?: string;
  children?: string | ReactElement<unknown>;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderType?: BorderStyleTypes;
  btnColor?: string;
  padding?: string;
  borderRadius?: string;
  fontWeight?: string;
  height?: string;
  width?: string;
  disabled?: boolean;
  readOnly?: boolean;
  // Method shorthand throughout this file: these are Syncfusion-era handler slots whose payload
  // call sites annotate more narrowly than the component supplies. Method syntax keeps the
  // parameter bivariant, which is the contract these props have always had.
  onBlur?(data?: React.FocusEvent<HTMLButtonElement>): unknown;
  onFocus?(data?: React.FocusEvent<HTMLButtonElement>): unknown;
  onClick?(data?: React.MouseEvent<HTMLButtonElement>): unknown;
  buttonType?: string;
  type?: string;
  textAlign?: string;
};

export interface SplitButtonItemsType {
  id?: string;
  text?: string;
  iconCss?: string;
}
export type DropdownButtonItemsType = SplitButtonItemsType;
export interface DropdownButtonComponentProps {
  beforeItemRender?(data: unknown): unknown;
  items?: DropdownButtonItemsType[];
  children?: string | ReactElement<unknown>;
  isDisabled?: boolean;
  iconCss?: string;
  color?: string;
  label?: string;
  height?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  buttonType?: string;
  content?: string;
  popUpWidth?: string;
  maxPopUpHeight?: string;
  width?: string;
  allowButtonPadding?: boolean;
  onSelect?(data?: unknown): unknown;
  onBlur?(data?: unknown): unknown;
  onChange?(data?: unknown): unknown;
  onClick?(data?: unknown): unknown;
  onFocus?(data?: unknown): unknown;
  id?: string;
}

export interface DropdownListComponentProps {
  ref?: React.Ref<unknown>;
  id?: string;
  /** Option objects, or a plain string array; the component normalises both. */
  dataSource: unknown[];
  textColor?: string;
  popupHeight?: string;
  popupWidth?: string;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  /** Field-name map: which datasource keys hold the display text and the value. */
  fields?: { text?: string; value?: string };
  /** Nullish = nothing selected yet; the component resolves `value == null` to no option. */
  value: string | null | undefined;
  setValue?(data: { value: unknown; itemData: Record<string, unknown> }): void;
  width?: string;
  iconCss?: string;
  color?: string;
  label?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  buttonType?: string;
  border?: string;
  allowFlexGrow?: boolean;
  /**
   * METHOD shorthand + all-optional members. The component emits `{ value, itemData }`, but call
   * sites annotate this parameter differently (`{ value: ObjectIdString; itemData }` in
   * VariableRateModal, `CurrencyChangeEvent` in CurrencySetting). A property arrow made the
   * parameter contravariant, so neither fitted and this used to be `any`; method syntax makes it
   * bivariant, and every member being optional means each call site's shape is assignable TO this
   * one, which is the direction bivariance needs.
   */
  onChange?(e: { value?: unknown; itemData?: Record<string, unknown> }): void;
  disabled?: boolean;
  name?: string;
}

export interface ToggleSwitchComponentProps {
  id?: string | undefined;
  label?: string | undefined;
  checked?: boolean | undefined;
  isChecked?: boolean | undefined;
  isDisabled?: boolean | undefined;
  disabled?: boolean | undefined;
  gradientColor1?: string | undefined;
  gradientColor2?: string | undefined;
  bgColor?: string | undefined;
  borderColor?: string | undefined;
  // The wrapper hands `onChange || change` straight to MUI's Switch, whose handler is
  // `(event, checked)`.
  onBlur?(data?: React.FocusEvent<HTMLInputElement>): unknown;
  onChange?(_: React.ChangeEvent<HTMLInputElement>, data: boolean): unknown;
  // The only call site annotates this as `React.SyntheticEvent`, which is also what MUI's Switch
  // hands over, so that is the declared payload.
  onClick?(data: React.SyntheticEvent): unknown;
  onFocus?(data?: React.FocusEvent<HTMLInputElement>): unknown;
  change?(_: React.ChangeEvent<HTMLInputElement>, data: boolean): unknown;
}
export interface CheckboxComponentProps {
  checked?: boolean;
  label?: string;
  borderColor?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  name?: string;
  value?: unknown;
  isDisabled?: boolean;
  onBlur?(data?: unknown): unknown;
  onChange?(data?: unknown): unknown;
  onClick?(data?: unknown): unknown;
  onFocus?(data?: unknown): unknown;
  change?(data?: unknown): unknown;
  id?: string;
}

export interface MultiSelectComponentProps {
  id?: string;
  /** Option objects, or a plain string array; the component normalises both. */
  dataSource?: unknown[];
  mode?: "Box" | "Delimiter" | "Default" | "CheckBox";
  /** Field-name map: which datasource keys hold the display text and the value. */
  fields?: { text?: string; value?: string };
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  enabled?: boolean;
  widthInPercentage?: string;
  margin?: string;
  disableChipBackground?: boolean;
  // The parameter is NOT optional and every member of the payload IS optional. Both halves matter:
  // a `data?:` puts `| undefined` in the comparison and an optional-vs-required member mismatch
  // (`itemData` here) closes the other direction, so the two together defeated bivariance in both
  // directions. All-optional members mean each call site's own payload -- ModelComparison's
  // `{ value: number; itemData?: SourceFileVersionData | null }`, AttachIssues' `AttachIssuesChange`
  // -- is assignable TO this one, which is the direction bivariance can use.
  change?(data: { value?: unknown; itemData?: unknown }): unknown;
  onChange?(data: { value?: unknown; itemData?: unknown }): unknown;
  onClick?(data?: React.MouseEvent<HTMLElement>): unknown;
  onFocus?(data?: React.FocusEvent<HTMLElement>): unknown;
}
export interface TextboxComponentProps {
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  widthInPercentage?: string;
  isDisabled?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  isWarning?: boolean;
  isRTL?: boolean;
  isSmall?: boolean;
  label?: string;
  margin?: string;
  name?: string;
  value?: string;
  type?: string;
  floatLabelType?: string;
  onChange?(data?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
  onFocus?(data?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
  onBlur?(data?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
  // Declared over `Element`, not the input: `TextBoxComponent` spreads these onto MUI's TextField,
  // which binds key handlers to its ROOT div (`KeyboardEventHandler<HTMLDivElement>`) and lets the
  // input's events bubble to it. Naming the input made the two signatures disjoint in BOTH
  // directions, so bivariance could not bridge them either.
  onKeyDown?(data?: React.KeyboardEvent<Element>): unknown;
  onKeyUp?(data?: React.KeyboardEvent<Element>): unknown;
  // Parameter name kept verbatim (capital D) - it is part of the published surface.
  change?(Data?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
  ref?: React.Ref<HTMLInputElement>;
}

export interface TextboxMaskComponentProps {
  mask?: MaskTypes;
  isDisabled?: boolean;
  widthInPercentage?: string;
  margin?: string;
  label?: string;
  onChange?(data?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
  onFocus?(data?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
  onBlur?(data?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
}

export interface TextboxNumericComponentProps {
  format?: string;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
  widthInPercentage?: string;
  widthInPX?: string;
  margin?: string;
  showSpinButton?: boolean;
  /**
   * `target` is declared because the component underneath (`NumericTextBoxComponent.emitChange`)
   * always emits `{ value, previousValue, event, target }`, and handlers read `e.target.value` (the five
   * in `ReviewFlowReviewLevelProperties` do). Non-optional because it is always emitted.
   */
  onChange?(data: { value: number | null; target: { value: string; name?: string } }): void;
}

export interface AutoCompleteComponentProps {
  id?: string;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  isEnabled?: boolean;
  widthInPercentage?: string;
  /** Option objects, or a plain string array; the component normalises both. */
  dataSource: unknown[];
  /** All-optional members, for the same variance reason as MultiSelectComponentProps.change. */
  change?(e: { value?: unknown; itemData?: unknown }): unknown;
}

export type ComboBoxComponentProps = AutoCompleteComponentProps & {
  popupHeight?: string | undefined;
  margin?: string | undefined;
  dataSource?: unknown[] | undefined;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString | undefined;
  // `| undefined` as well as optional: callers bind this to an optional model field (the selected
  // symbol's name, a unit id), so the prop genuinely arrives holding `undefined`.
  value?: string | number | undefined;
  isEnabled?: boolean | undefined;
};

export interface RichTextEditorComponentProps {
  id?: string;
  enableResize?: boolean;
  showCharCount?: boolean;
  enabled?: boolean;
  height?: string;
  width?: string;
  items?: string[];
  iconColor?: string;
  // DEAD as of today: the only reference to this interface is `defaultRichTextEditorComponentProps`
  // in globalVariable.tsx, which nothing imports.
  change?(data?: unknown): unknown;
  focus?(data?: unknown): unknown;
  blur?(data?: unknown): unknown;
  actionBegin?(data?: unknown): unknown;
  actionComplete?(data?: unknown): unknown;
  padding?: string;
  children?: React.ReactNode;
  allowPadding?: boolean;
}

export interface IconComponentProps {
  name?: string | undefined;
  height?: string | number | undefined;
  className?: string | undefined;
  showGradient?: boolean | undefined;
  /** Tooltip text - must be wrapped in t() for internationalization */
  toolTipContent?: TranslatedString | undefined;
  position?: string | undefined;
  children?: React.ReactNode | undefined;
  menuShow?: boolean | undefined;
  isVisible?: boolean | undefined;
  isEnabled?: boolean | undefined;
  type?: string | undefined;
  width?: string | undefined;
  parentContainerClass?: string | undefined;
  dataTestId?: string | undefined;
  // Declared non-optional and with the element the icon actually renders, which is what the
  // component hands over. Method shorthand keeps the parameter bivariant, so the ~20 call sites
  // that annotate their handler as `React.MouseEvent`, `React.SyntheticEvent` or a bare
  // `{ stopPropagation }` bag all still satisfy it, while inline arrows get a parameter concrete
  // enough to forward to helpers that expect an `HTMLElement` event.
  onClick?(data: React.MouseEvent<HTMLElement>): unknown;
  color?: string | undefined;
  onMouseDown?(data?: React.MouseEvent<HTMLElement>): unknown;
}

/**
 * Selection payload `ListBoxComponent` emits; `items` always holds exactly the clicked row. Every
 * other member is optional so that call sites which annotate their handler with just the slice they
 * read (e.g. `{ items: [StatusListEntry] }` in ConfigureReviewFlowStatusList) stay compatible.
 */
export interface ListBoxChangeArgs {
  value?: unknown;
  text?: unknown;
  index?: number;
  itemData?: Record<string, unknown>;
  items: Record<string, unknown>[];
}

export interface ListBoxComponentProps {
  /** Same payload as `change` below; method shorthand keeps the parameter bivariant. */
  onChange?(data: ListBoxChangeArgs): unknown;
  change?(data: ListBoxChangeArgs): unknown;
  dataSource: Array<Record<string, unknown>>;
  selectedIndex?: number;
  /** Either a class name or an `sx`-style object; the component only spreads it when it is an object. */
  listBoxStyle?: string | Record<string, unknown>;
  fields?: { text?: string; value?: string };
  itemTemplate?(item: Record<string, unknown>): React.ReactNode;
  height?: string | number;
}
export declare type TooltipPosition =
  | "bottom-end"
  | "bottom-start"
  | "bottom"
  | "left-end"
  | "left-start"
  | "left"
  | "right-end"
  | "right-start"
  | "right"
  | "top-end"
  | "top-start"
  | "top";
export interface TooltipComponentProps {
  position?: TooltipPosition;
  children?: React.ReactNode;
  /** Tooltip content - strings must be wrapped in t() for internationalization */
  content?: TranslatedString | React.ReactElement | null | undefined;
  /** Forwarded to the underlying MUI Tooltip: tooltip follows the cursor. */
  followCursor?: boolean;
  /** Forwarded to the underlying MUI Tooltip. */
  slotProps?: {
    popper?: { sx?: Record<string, unknown>; style?: React.CSSProperties } & Record<string, unknown>;
  } & Record<string, unknown>;
  tipColor?: string;
  color?: string;
  bgColor?: string;
  fontSize?: string;
  width?: string;
  style?: React.CSSProperties;
  showTipPointer?: boolean;
  maxHeight?: string;
  /** Forwarded to the underlying MUI Tooltip: suppresses the hover trigger, leaving focus/touch. */
  disableHoverListener?: boolean;
  /** Forwarded to MUI's Tooltip. */
  arrow?: boolean;
  className?: string;
  /** Merged into the tooltip surface's style block by the styled() wrapper. */
  tooltipStyles?: React.CSSProperties;
  open?: boolean;
  onOpen?: (event: React.SyntheticEvent) => void;
  onClose?: (event: React.SyntheticEvent | Event) => void;
}

export interface CardComponentProps {
  // Every member carries `| undefined`: a card's content and its chrome flags are built from the
  // caller's own optional data (`widget.title`, a maybe-absent link, a handler only some hosts
  // supply), and each one falls through to the same default whether the key is absent or `undefined`.
  /** Opaque payload echoed back as the second argument of `onClick`. */
  dataBound?: unknown;
  cardBody?: string | ReactElement<unknown> | undefined;
  width?: string | undefined;
  background?: string | undefined;
  // `ReactNode`, not `string | ReactElement`: `NotificationToggleCard` passes `false` to suppress the
  // header, and `dashboardWidgetCard` passes `widget.title || _.get(widget, "data.title")`.
  cardHeaderText?: React.ReactNode | undefined;
  // ⚠️ Declared and passed (globalVariable.tsx:374, ProjectsList/GridView.tsx:299) but never read by
  // card/index.tsx: the MUI card dropped the Syncfusion "View all" text link in favour of the
  // `onViewCLick` icon button, and `e-card-header-link` is now only the max/min + three-dots container.
  // A caller that sets this gets no link and no error. Either render it or delete it with its two call
  // sites; packages/storybook CardComponent still implements the real link if the text one is wanted back.
  headerLinkText?: string | undefined;
  headerBackgroundColor?: string | undefined;
  enableShareIcon?: boolean | undefined;
  maxMinIconName?: string | undefined;
  enableMaxMinIcon?: boolean | undefined;
  enableThreeDotsIcon?: boolean | undefined;
  /** Three-dots menu entries, forwarded to `SelectDropDown`'s `items`. */
  items?: Array<Record<string, unknown>> | undefined;
  /** `null` is a real value here - `defaultCardComponentProps` sets it. */
  onClick?: ((e: React.SyntheticEvent, dataBound?: unknown) => void) | null | undefined;
  /** Forwarded to `SelectDropDown`'s `select`. */
  /**
   * The item-select payload of the card's `SelectDropDown`. Both members carry `| undefined`: the
   * dropdown's own `SelectEventArgs.item` (Mui_Form/SelectDropDown.tsx) declares them that way, and a
   * parameter is checked contravariantly, so an exact-optional copy here rejects the very args the
   * dropdown hands back.
   */
  selectDropDownItem?(args: { item: { id?: string | undefined; text?: string | undefined } }): void;
  onMaximizeClick?(data?: React.MouseEvent<HTMLButtonElement>): unknown;
  onViewCLick?: (() => unknown) | undefined;
  elevation?: number | undefined;
}

export declare type AvatarSizeType = "XXS" | "SM" | "LSM" | "XS" | "LG" | "LLG" | "XL";
export interface AvatarComponentProps {
  isCircle?: boolean;
  isSquare?: boolean;
  size?: AvatarSizeType;
  children?: string | ReactElement<unknown>;
  bgColor?: string;
  style?: React.CSSProperties;
}

export interface SliderComponentProps {
  // DEAD as of today: `cubeComponent/form/SliderComponent` imports its props from
  // `Mui_Form/SliderComponent`, so the only reference to this interface is
  // `defaultSliderComponentProps` in globalVariable.tsx.
  onChange?(data?: { value: number | number[] }): unknown;
  onClick?(data?: React.MouseEvent<HTMLElement>): unknown;
  gradientColor1?: string;
  gradientColor2?: string;
  handleGradientColor1?: string;
  handleGradientColor2?: string;
  handleShadowColor?: string;
  color?: string;
  enabled?: boolean;
  min?: number;
  max?: number;
  value?: number;
  width?: number | string;
}

export interface ModalStateDialogComponentProps {
  headerContent?: (TranslatedString | ReactElement<unknown>) | undefined;
  footerContent?: (TranslatedString | ReactElement<unknown>) | undefined;
  modelContent?: (TranslatedString | ReactElement<unknown>) | undefined;
  isVisible?: boolean | undefined;
  width?: string | undefined;
  minWidth?: string | undefined;
  id?: string | undefined;
  ref?: React.Ref<unknown> | undefined;
  disableCrossIcon?: boolean | undefined;
  /** Modal title - strings must be wrapped in t() for internationalization */
  headerTitle?: (TranslatedString | ReactElement<unknown>) | undefined;
  onSave?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  allowModalPadding?: boolean | undefined;
  modalPadding?: number | undefined;
  zindex?: number | undefined;
  height?: (string | number) | undefined;
  headerColor?: string | undefined;
  footerBorderTop?: boolean | undefined;
  footerBorderColor?: string | undefined;
}

/**
 * Syncfusion-era grid surface. The wrapper (`html/table`) reads `headerColumns` and `dataSource`, and
 * declares its remote pair (`query` / `remoteDataSource`) itself, typed by SlickGrid1; the styling/cell
 * props below are accepted and forwarded but nothing consumes them today.
 */
export interface TableComponentProps {
  width?: string;
  body?: React.ReactNode;
  headerColumns: GridHeaderColumnsType[];
  dataSource?: unknown[];
  height?: string | number;
  field?: string;
  textAlign?: string;
  headerColor?: string;
  queryCellInfo?(args: unknown): void;
  background?: string;
  borderColor?: string;
}

export interface GridHeaderColumnsType {
  field: string;
  headerText: string;
  width?: string;
  textAlign?: TextAlign;
  /** Cell renderer; handed to the grid as its column `formatter`. */
  template?(...args: unknown[]): unknown;
  /** Overflow behaviour for the cell; defaults to "EllipsisWithTooltip" in the wrapper. */
  clipMode?: string;
  /** `false` disables the column's filter / sort entry in the grid toolbar. */
  allowFiltering?: boolean;
  allowSorting?: boolean;
}

export interface DropdownIconComponentProps {
  onClick?(data?: React.MouseEvent<HTMLElement>): unknown;
  popUpWidth?: string;
  /** Menu entries; each is rendered as `text`, else `label`, else the entry itself. */
  dataSource?: unknown[];
}

export interface DragListComponentProps<T extends Record<string, unknown> = Record<string, unknown>> {
  data1?: T[];
  data2?: T[];
  /** Field-name map, e.g. `{ text: "name", value: "id" }`. `text` selects the display field, `value` the key field. */
  fields?: Record<string, string>;
  setData1?: (value: T[]) => void;
  setData2?: (value: T[]) => void;
  itemTemplate?: (item: T) => React.ReactNode;
  /** Show the centre column of transfer / reorder buttons (move, move-all, up, down). Default false. */
  showButtons?: boolean;
  /** Allow selecting multiple list items at once (click toggles selection). Default false. */
  allowMultiSelect?: boolean;
  /** Show a search box above each list to filter its items. Default false. */
  showSearch?: boolean;
  /** Heading shown above the first (left) list. Default "Available". */
  availableHeader?: string;
  /** Heading shown above the second (right) list. Default "Selected". */
  selectedHeader?: string;
  /** Placeholder text for the search inputs. Default "Search". */
  searchPlaceholder?: string;
  /** Show the "Drag and drop or select…" helper line above the lists. Default true. */
  showHint?: boolean;
  /**
   * Fixed height of each list box (any CSS length) — both boxes get the same height so they stay
   * aligned. Ignored when `fillHeight` is set. Default "30rem" (480px).
   */
  listHeight?: string | number;
  /**
   * Make each list box flex to fill available height and scroll internally, instead of a fixed
   * `listHeight`. Use inside a height-bounded container (e.g. a fixed-height modal). The parent
   * MUST supply a bounded height down to this component or the boxes collapse.
   */
  fillHeight?: boolean;
  noItemsText?: string;
}

/**
 * DEAD as of today: the only reference is `defaultMapComponentProps` in globalVariable.tsx, which
 * nothing imports. Field types are taken from that literal.
 */
export interface MapComponentProps {
  latitude?: number;
  longitude?: number;
  zoomFactor?: number;
  enable?: boolean;
  height?: string | number;
  width?: string | number;
  name?: string;
  isToolTipVisible?: boolean;
  mapOnClick?(data: unknown): unknown;
}
